import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Users, DollarSign, Swords, Gem, CreditCard, Shield, 
  AlertTriangle, Ban, RefreshCw, ArrowLeft, Lock, Unlock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin wallet placeholder - user will set this
const ADMIN_WALLET = process.env.REACT_APP_ADMIN_WALLET || 'PLACEHOLDER_ADMIN_WALLET';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { walletAddress, isAuthenticated } = useAuth();
  
  // State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Form states
  const [banTarget, setBanTarget] = useState('');
  const [banReason, setBanReason] = useState('');
  const [cardUpdateId, setCardUpdateId] = useState('');
  const [newPower, setNewPower] = useState(3);
  
  // Verify admin access
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const verifyAdmin = async () => {
      if (!walletAddress) {
        setIsVerifying(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/admin/verify?wallet_address=${walletAddress}`);
        setIsAdmin(response.data.is_admin);
        
        if (response.data.is_admin) {
          fetchMetrics();
          fetchUsers();
        }
      } catch (error) {
        console.error('Admin verification error:', error);
        setIsAdmin(false);
      }
      
      setIsVerifying(false);
    };
    
    verifyAdmin();
  }, [walletAddress]);
  /* eslint-enable react-hooks/exhaustive-deps */
  
  // Fetch admin metrics
  const fetchMetrics = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/metrics?wallet_address=${walletAddress}`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to load metrics');
    }
    setIsLoading(false);
  };
  
  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?wallet_address=${walletAddress}`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  // Ban user
  const handleBanUser = async () => {
    if (!banTarget) {
      toast.error('Please enter a user ID');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/ban-user?wallet_address=${walletAddress}&target_user=${banTarget}&reason=${encodeURIComponent(banReason)}`);
      toast.success(`User ${banTarget} banned`);
      setBanTarget('');
      setBanReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };
  
  // Unban user
  const handleUnbanUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/unban-user?wallet_address=${walletAddress}&target_user=${userId}`);
      toast.success(`User ${userId} unbanned`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };
  
  // Update card power
  const handleUpdateCardPower = async () => {
    if (!cardUpdateId) {
      toast.error('Please enter a card ID');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/update-card-power?wallet_address=${walletAddress}&card_id=${cardUpdateId}&new_power=${newPower}`);
      toast.success(`Card power updated to ${newPower}`);
      setCardUpdateId('');
      setNewPower(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update card');
    }
  };
  
  // Clear news cache
  const handleClearCache = async () => {
    try {
      await axios.post(`${API}/news/rss/clear-cache`);
      toast.success('News cache cleared');
    } catch (error) {
      toast.error('Failed to clear cache');
    }
  };
  
  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-neon-pink animate-spin mx-auto mb-4" />
          <p className="font-space text-white/60">Verifying admin access...</p>
        </div>
      </div>
    );
  }
  
  // Not authenticated
  if (!isAuthenticated || !walletAddress) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8 bg-black/60 border border-red-500/30 rounded-2xl">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-anton text-3xl text-white mb-2">ACCESS DENIED</h1>
          <p className="font-space text-white/60 mb-6">
            Please connect your wallet to access the admin dashboard.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }
  
  // Not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8 bg-black/60 border border-red-500/30 rounded-2xl">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-anton text-3xl text-white mb-2">ADMIN ONLY</h1>
          <p className="font-space text-white/60 mb-4">
            Your wallet address does not have admin access.
          </p>
          <p className="font-mono text-xs text-white/40 break-all mb-6">
            {walletAddress}
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              className="bg-white/10 hover:bg-white/20"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-anton text-2xl flex items-center gap-2">
                <Shield className="w-6 h-6 text-neon-pink" />
                ADMIN DASHBOARD
              </h1>
              <p className="font-space text-xs text-white/40">God Mode Enabled</p>
            </div>
          </div>
          
          <Button onClick={fetchMetrics} disabled={isLoading} className="bg-neon-green/20 hover:bg-neon-green/30 text-neon-green">
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Metrics Grid */}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <MetricCard
              icon={<Users className="w-6 h-6" />}
              label="Total Users"
              value={metrics.total_users}
              color="cyan"
            />
            <MetricCard
              icon={<Users className="w-6 h-6" />}
              label="Active Users (DAU)"
              value={metrics.daily_active_users}
              color="green"
            />
            <MetricCard
              icon={<DollarSign className="w-6 h-6" />}
              label="Revenue (Tokens)"
              value={metrics.total_revenue_tokens?.toLocaleString() || 0}
              color="yellow"
            />
            <MetricCard
              icon={<Gem className="w-6 h-6" />}
              label="Shards in Circulation"
              value={metrics.total_shards_circulation?.toLocaleString() || 0}
              color="pink"
            />
            <MetricCard
              icon={<CreditCard className="w-6 h-6" />}
              label="Total Cards"
              value={metrics.total_cards}
              color="purple"
            />
            {metrics.cards_by_rarity && Object.entries(metrics.cards_by_rarity).map(([rarity, count]) => (
              <MetricCard
                key={rarity}
                icon={<CreditCard className="w-6 h-6" />}
                label={`${rarity.charAt(0).toUpperCase() + rarity.slice(1)} Cards`}
                value={count}
                color={rarity === 'legendary' ? 'yellow' : rarity === 'epic' ? 'pink' : rarity === 'rare' ? 'cyan' : 'gray'}
              />
            ))}
          </div>
        )}
        
        {/* Admin Controls */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Ban User */}
          <div className="bg-black/40 border border-red-500/30 rounded-xl p-6">
            <h2 className="font-anton text-xl text-red-400 flex items-center gap-2 mb-4">
              <Ban className="w-5 h-5" />
              Ban User
            </h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="User ID / Wallet Address"
                value={banTarget}
                onChange={(e) => setBanTarget(e.target.value)}
                className="w-full bg-black/60 border border-white/20 rounded-lg px-4 py-2 font-space text-sm"
              />
              <input
                type="text"
                placeholder="Reason (optional)"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="w-full bg-black/60 border border-white/20 rounded-lg px-4 py-2 font-space text-sm"
              />
              <Button onClick={handleBanUser} className="w-full bg-red-600 hover:bg-red-500">
                <Ban className="w-4 h-4 mr-2" />
                Ban User
              </Button>
            </div>
          </div>
          
          {/* Update Card Power */}
          <div className="bg-black/40 border border-yellow-500/30 rounded-xl p-6">
            <h2 className="font-anton text-xl text-yellow-400 flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5" />
              Force Update Card Power
            </h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Card ID"
                value={cardUpdateId}
                onChange={(e) => setCardUpdateId(e.target.value)}
                className="w-full bg-black/60 border border-white/20 rounded-lg px-4 py-2 font-space text-sm"
              />
              <div className="flex items-center gap-4">
                <label className="font-space text-sm text-white/60">New Power:</label>
                <select
                  value={newPower}
                  onChange={(e) => setNewPower(parseInt(e.target.value))}
                  className="bg-black/60 border border-white/20 rounded-lg px-4 py-2 font-space text-sm"
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} Stars</option>
                  ))}
                </select>
              </div>
              <Button onClick={handleUpdateCardPower} className="w-full bg-yellow-600 hover:bg-yellow-500 text-black">
                <RefreshCw className="w-4 h-4 mr-2" />
                Update Card
              </Button>
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-black/40 border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="font-anton text-xl text-white mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <Button onClick={handleClearCache} className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 border border-purple-500/30">
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear News Cache
            </Button>
          </div>
        </div>
        
        {/* Recent Battles */}
        {metrics?.recent_battles && (
          <div className="bg-black/40 border border-white/10 rounded-xl p-6 mb-8">
            <h2 className="font-anton text-xl text-white flex items-center gap-2 mb-4">
              <Swords className="w-5 h-5 text-cyan-400" />
              Recent PvP Battles
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left font-space text-xs text-white/40 py-2">Battle ID</th>
                    <th className="text-left font-space text-xs text-white/40 py-2">Player</th>
                    <th className="text-left font-space text-xs text-white/40 py-2">Score</th>
                    <th className="text-left font-space text-xs text-white/40 py-2">Status</th>
                    <th className="text-left font-space text-xs text-white/40 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.recent_battles.map((battle, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="font-mono text-xs text-white/60 py-2">{battle.battle_id?.slice(0, 8)}...</td>
                      <td className="font-mono text-xs text-white/60 py-2">{battle.session_id?.slice(0, 12)}...</td>
                      <td className="font-space text-sm text-white py-2">{battle.player_score || 0} - {battle.bot_score || 0}</td>
                      <td className="py-2">
                        <span className={`font-space text-xs px-2 py-1 rounded ${
                          battle.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {battle.status}
                        </span>
                      </td>
                      <td className="font-space text-xs text-white/40 py-2">{new Date(battle.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Users List */}
        <div className="bg-black/40 border border-white/10 rounded-xl p-6">
          <h2 className="font-anton text-xl text-white flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-cyan-400" />
            Users ({users.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left font-space text-xs text-white/40 py-2">User ID</th>
                  <th className="text-left font-space text-xs text-white/40 py-2">Shards</th>
                  <th className="text-left font-space text-xs text-white/40 py-2">$SLAP</th>
                  <th className="text-left font-space text-xs text-white/40 py-2">PvP W/L</th>
                  <th className="text-left font-space text-xs text-white/40 py-2">Status</th>
                  <th className="text-left font-space text-xs text-white/40 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="font-mono text-xs text-white/60 py-2">{user.session_id?.slice(0, 16)}...</td>
                    <td className="font-space text-sm text-neon-green py-2">{user.shards || 0}</td>
                    <td className="font-space text-sm text-yellow-400 py-2">{user.slap_tokens || 0}</td>
                    <td className="font-space text-sm text-white py-2">{user.pvp_wins || 0} / {user.pvp_losses || 0}</td>
                    <td className="py-2">
                      <span className={`font-space text-xs px-2 py-1 rounded ${
                        user.is_banned ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                      }`}>
                        {user.is_banned ? 'Banned' : 'Active'}
                      </span>
                    </td>
                    <td className="py-2">
                      {user.is_banned ? (
                        <Button
                          onClick={() => handleUnbanUser(user.session_id)}
                          size="sm"
                          className="bg-green-600/20 hover:bg-green-600/40 text-green-400 text-xs"
                        >
                          <Unlock className="w-3 h-3 mr-1" />
                          Unban
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setBanTarget(user.session_id)}
                          size="sm"
                          className="bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs"
                        >
                          <Ban className="w-3 h-3 mr-1" />
                          Ban
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    cyan: 'border-cyan-500/30 text-cyan-400',
    green: 'border-neon-green/30 text-neon-green',
    yellow: 'border-yellow-500/30 text-yellow-400',
    pink: 'border-pink-500/30 text-pink-400',
    purple: 'border-purple-500/30 text-purple-400',
    gray: 'border-white/20 text-white/60',
  };
  
  return (
    <motion.div
      className={`bg-black/40 border ${colorClasses[color]} rounded-xl p-4`}
      whileHover={{ scale: 1.02 }}
    >
      <div className={`${colorClasses[color]} mb-2`}>{icon}</div>
      <p className="font-space text-xs text-white/40 mb-1">{label}</p>
      <p className={`font-anton text-2xl ${colorClasses[color]}`}>{value}</p>
    </motion.div>
  );
};

export default AdminDashboard;
